import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {api, jsonOptions, tokenKey} from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({children}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(localStorage.getItem(tokenKey)));
  const profileChecked = useRef(false);
  useEffect(() => {
    const expire = () => { setUser(null); setLoading(false); };
    window.addEventListener('lehaq:session-expired', expire);
    return () => window.removeEventListener('lehaq:session-expired', expire);
  }, []);
  useEffect(() => {
    if (profileChecked.current) return;
    profileChecked.current = true;
    if (!localStorage.getItem(tokenKey)) { setLoading(false); return; }
    api('/me').then(response => setUser(response.data)).catch(() => localStorage.removeItem(tokenKey)).finally(() => setLoading(false));
  }, []);
  const accept = useCallback(payload => {
    localStorage.setItem(tokenKey, payload.data.token);
    setUser(payload.data.user);
    return payload.data.user;
  }, []);
  const login = useCallback(async credentials => accept(await api('/auth/login', jsonOptions('POST', credentials))), [accept]);
  const register = useCallback(async data => accept(await api('/auth/register', jsonOptions('POST', data))), [accept]);
  const requestOtp = useCallback(async data => (await api('/auth/otp/request', jsonOptions('POST', data))).data, []);
  const verifyOtp = useCallback(async data => (await api('/auth/otp/verify', jsonOptions('POST', data))).data, []);
  const loginWithOtp = useCallback(async challengeId => accept(await api('/auth/otp/login', jsonOptions('POST', {challenge_id: challengeId}))), [accept]);
  const registerWithPhone = useCallback(async data => accept(await api('/auth/register/phone', jsonOptions('POST', data))), [accept]);
  const resetPassword = useCallback(async data => api('/auth/password/reset', jsonOptions('POST', data)), []);
  const updateProfile = useCallback(async data => { const payload = await api('/me', jsonOptions('PATCH', data)); setUser(payload.data); return payload.data; }, []);
  const logout = useCallback(async () => { try { await api('/auth/logout', {method: 'POST'}); } finally { localStorage.removeItem(tokenKey); setUser(null); } }, []);
  const value = useMemo(() => ({user, loading, login, register, requestOtp, verifyOtp, loginWithOtp, registerWithPhone, resetPassword, updateProfile, logout, authenticated: Boolean(user)}), [user, loading, login, register, requestOtp, verifyOtp, loginWithOtp, registerWithPhone, resetPassword, updateProfile, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
